/*
  Matching for the palette: scoring pages / actions against what was typed and highlighting the typed words in result
  texts. Case- and accent-insensitive ("cafe" lights up "Café"), like the server search.
*/

const stripAccents = (text) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const fold = (text) => stripAccents(String(text ?? '')).toLowerCase();

// folded text plus, per folded character, the index of the original character it came from
function foldWithMap(text) {
  const source = String(text ?? '');
  let folded = '';
  const map = [];
  for (let index = 0; index < source.length; index += 1) {
    const part = fold(source[index]);
    for (let k = 0; k < part.length; k += 1) {
      folded += part[k];
      map.push(index);
    }
  }
  return { folded, map };
}

export const queryWords = (text) => fold(text).split(/\s+/).filter(Boolean);

/*
  Text split into [{ text, hit }] parts where every occurrence of one of the words is a hit.
  Used for titles, subtitles and "found in" snippets.
*/
export function highlight(text, words) {
  const source = String(text ?? '');
  if (!source || !words?.length) return [{ text: source, hit: false }];
  const { folded, map } = foldWithMap(source);
  const ranges = [];
  for (const word of words) {
    const needle = fold(word);
    if (!needle) continue;
    let from = folded.indexOf(needle);
    while (from >= 0) {
      ranges.push([map[from], map[from + needle.length - 1] + 1]);
      from = folded.indexOf(needle, from + needle.length);
    }
  }
  if (!ranges.length) return [{ text: source, hit: false }];
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [ranges[0]];
  for (const [start, end] of ranges.slice(1)) {
    const last = merged[merged.length - 1];
    if (start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }
  const parts = [];
  let cursor = 0;
  for (const [start, end] of merged) {
    if (start > cursor) parts.push({ text: source.slice(cursor, start), hit: false });
    parts.push({ text: source.slice(start, end), hit: true });
    cursor = end;
  }
  if (cursor < source.length) parts.push({ text: source.slice(cursor), hit: false });
  return parts;
}

// characters of `needle` in order inside `haystack` ("inin" -> "invoices in"), more for runs of adjacent ones
function subsequenceScore(needle, haystack) {
  if (needle.length < 2) return 0;
  let score = 0;
  let run = 0;
  let position = 0;
  for (const ch of needle) {
    const found = haystack.indexOf(ch, position);
    if (found < 0) return 0;
    run = found === position ? run + 1 : 1;
    score += run;
    position = found + 1;
  }
  return score;
}

const splitWords = (text) => text.split(/[\s:&/(),.-]+/).filter(Boolean);

/*
  How well a page / action matches the typed text, 0 (no match) to 100 (exactly its title).
  >= 80 counts as a strong match: the palette then lists pages above the search results.
*/
export function scoreCommand(query, command) {
  const q = fold(query).trim();
  if (!q) return 0;
  const title = fold(command.title);
  const words = q.split(/\s+/).filter(Boolean);
  const titleWords = splitWords(title);
  const keywords = (command.keywords || []).map(fold);
  const keywordWords = keywords.flatMap(splitWords);

  if (title === q) return 100;
  if (title.startsWith(q)) return 92;
  if (titleWords.some((word) => word.startsWith(q))) return 84;
  if (words.every((word) => titleWords.some((titleWord) => titleWord.startsWith(word)))) return 80;
  if (title.includes(q)) return 66;
  if (keywords.some((keyword) => keyword === q || keyword.startsWith(q))) return 62;
  if (words.every((word) => [...titleWords, ...keywordWords].some((candidate) => candidate.startsWith(word)))) return 56;
  if (keywords.some((keyword) => keyword.includes(q))) return 44;
  const fuzzy = subsequenceScore(q.replace(/\s+/g, ''), title.replace(/\s+/g, ''));
  return fuzzy >= q.replace(/\s+/g, '').length + 2 ? Math.min(36, 10 + fuzzy) : 0;
}

// the commands matching the query, best first
export function rankCommands(query, commands, limit = 6) {
  return commands
    .map((command) => ({ command, score: scoreCommand(query, command) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.command.title.localeCompare(b.command.title))
    .slice(0, limit);
}
